/**
 * "Strategy it supports" on the item page's Connections tab.
 *
 * Read from the top down: one row per strategy, the objectives under it that
 * the item supports (with the objectives above each), and each objective's
 * goals — the ones the item is linked to first. The strategy is shown, never
 * linked: the item supports an objective, and may also be linked to a goal
 * under that objective.
 *
 * The picker works three ways: open a strategy and see its objectives and
 * goals; pick an objective; or pick a goal (which links its objective too).
 * Searching finds any of them by name.
 */
import { useMemo, useState, type ReactNode } from 'react';
import { ObjectRef } from '@/components/ds/ObjectRef';
import { TextInput } from '@/components/ds';
import { btnSecondary } from '@/components/mono/primitives';
import type { Goal, StrategicObjective, Strategy } from '@/lib/strategyAlignment';
import { linkableObjectives } from '@/apps/align/alignLinking';
import { ChoiceList, type ChoiceOption } from '@/components/shared/ChoiceList';
import { ActionLinkView, EditorButtons, EditorError, type ViewLinkRow } from './ItemDetailThirdPassView';
import { useEditorSave } from '@/components/detail-page/useEditorSave';
import { goalMeasure } from './goalMeasure';
import { objectivesAbove, strategyIdOf, type AlignmentStrength, type ItemObjectiveLink, type ItemStrategy, type ItemStrategyGroup } from './useItemStrategy';

const STRENGTH: Record<AlignmentStrength, { label: string; choice: string; description: string }> = {
  primary: { label: 'mainly supports', choice: 'Mainly', description: 'A main way this gets met.' },
  secondary: { label: 'also supports', choice: 'Also', description: 'Helps, alongside other work.' },
  indirect: { label: 'indirectly supports', choice: 'Indirectly', description: 'Helps through other work.' },
};

const NOT_PURSUED: Partial<Record<StrategicObjective['status'], string>> = {
  achieved: 'achieved',
  deferred: 'deferred',
  cancelled: 'cancelled',
};

const objectivesHref = (strategyId?: string | null) =>
  strategyId ? `/prioritization/strategy/objectives?strategyId=${encodeURIComponent(strategyId)}` : '/prioritization/strategy/objectives';

export interface StrategyActions {
  /** Links the item to an objective, or to a goal under it; resolves with the refusal reason, or null. */
  linkObjective: ((objectiveId: string, strength: AlignmentStrength, goalId?: string) => Promise<string | null>) | null;
  /** Removes all of an objective's link rows. */
  unlinkObjective: ((mappingIds: string[]) => void) | null;
  /** Removes one goal from the item; the objective stays linked (its last row keeps the objective, without the goal). */
  unlinkGoal: ((mappingId: string, objectiveRowCount: number) => void) | null;
  openObjectives: () => void;
}

function GoalLine({ goal, linked, onRemove }: { goal: Goal; linked: boolean; onRemove?: () => void }) {
  const measure = goalMeasure(goal);
  return (
    <div className="mt-1 flex flex-wrap items-baseline gap-x-1.5 text-[13px]" style={{ opacity: linked ? 1 : 0.75 }}>
      <ObjectRef kind="goal" name={goal.name} />
      {measure && <span className="text-[var(--text-dim)]">{measure}</span>}
      {linked && <span className="text-[var(--text-dim)]">· this work moves it</span>}
      {onRemove && <ActionLinkView action={{ label: 'Remove', onClick: onRemove }} />}
    </div>
  );
}

function ObjectiveBlock({ link, strategyId, a }: { link: ItemObjectiveLink; strategyId: string | null; a: StrategyActions }) {
  const { objective, above, strength, linkedGoals, otherGoals, rationale } = link;
  const unlink = a.unlinkObjective;
  const unlinkGoal = a.unlinkGoal;
  const off = NOT_PURSUED[objective.status];
  return (
    <div className="py-1">
      {above.length > 0 && (
        <div className="flex flex-wrap items-baseline gap-x-1.5 text-[12.5px] text-[var(--text-dim)]">
          {above.map((o, i) => (
            <span key={o.id} className="inline-flex items-baseline gap-1.5">
              {i > 0 && <span aria-hidden>›</span>}
              <ObjectRef kind="objective" name={o.name} hideWord href={objectivesHref(strategyId)} />
            </span>
          ))}
          <span aria-hidden>›</span>
        </div>
      )}
      <div className="flex flex-wrap items-baseline gap-x-1.5">
        <ObjectRef kind="objective" name={objective.name} href={objectivesHref(strategyId)} />
        <span className="text-[13px] text-[var(--text-dim)]">· this work {STRENGTH[strength].label} it</span>
        {off && <span className="text-[13px] text-[var(--mustard)]">· {off}</span>}
        {unlink && <ActionLinkView action={{ label: 'Remove', onClick: () => unlink(link.mappingIds) }} />}
      </div>
      {linkedGoals.map(({ goal, mappingId }) => (
        <GoalLine key={goal.id} goal={goal} linked onRemove={unlinkGoal ? () => unlinkGoal(mappingId, link.mappingIds.length) : undefined} />
      ))}
      {otherGoals.map((g) => (
        <GoalLine key={g.id} goal={g} linked={false} />
      ))}
      {rationale && <div className="mt-1 text-[13px] text-[var(--text-dim)]">Why: {rationale}</div>}
    </div>
  );
}

function GroupText({ group, a }: { group: ItemStrategyGroup; a: StrategyActions }) {
  return (
    <>
      {group.strategy ? (
        <ObjectRef kind="strategy" id={group.strategy.id} name={group.strategy.name} hideWord />
      ) : (
        <span className="text-[var(--text-dim)]">Not under a strategy</span>
      )}
      <div className="mt-1.5 space-y-2 border-l pl-3" style={{ borderColor: 'var(--rule)' }}>
        {group.objectives.map((link) => (
          <ObjectiveBlock key={link.objective.id} link={link} strategyId={group.strategy?.id ?? null} a={a} />
        ))}
      </div>
    </>
  );
}

// ── The picker ──────────────────────────────────────────────────────────────

interface Pick {
  objectiveId: string;
  goalId: string | null;
}

function PickOption({ on, disabled, onClick, children, note }: { on: boolean; disabled?: boolean; onClick: () => void; children: ReactNode; note?: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      disabled={disabled}
      {...btnSecondary('!h-auto w-full !justify-start gap-2 py-1.5 text-left font-sans text-sm normal-case tracking-normal')}
      style={{
        borderColor: on ? 'var(--slate)' : 'var(--rule-bright)',
        background: on ? 'var(--slate-dim)' : undefined,
        color: on ? 'var(--text-pri)' : 'var(--text-sec)',
        boxShadow: on ? 'inset 3px 0 0 var(--slate)' : undefined,
        opacity: disabled ? 0.55 : undefined,
      }}
      onClick={onClick}
    >
      <span className="min-w-0 flex-1">{children}</span>
      {note && <span className="shrink-0 text-[12px] text-[var(--text-dim)]">{note}</span>}
    </button>
  );
}

/** Pick an objective or a goal under one, starting from its strategy or by name; say how strongly; Link. */
export function StrategyLinkEditor({ s, close, save }: { s: ItemStrategy; close: () => void; save: NonNullable<StrategyActions['linkObjective']> }) {
  const byId = useMemo(() => new Map(s.objectives.map((o) => [o.id, o])), [s.objectives]);
  const linkedObjective = useMemo(() => new Map(s.groups.flatMap((g) => g.objectives).map((l) => [l.objective.id, l])), [s.groups]);
  const pickable = useMemo(() => linkableObjectives(s.objectives), [s.objectives]);
  const goalsOf = (objectiveId: string) => s.goals.filter((g) => g.objectiveId === objectiveId && g.status === 'active');

  // Strategy → its objectives, parents before children (indent by depth within the strategy).
  const tree = useMemo(() => {
    const groups: { strategy: Strategy | null; rows: { o: StrategicObjective; depth: number }[] }[] = [];
    const keyOf = (o: StrategicObjective) => strategyIdOf(o, byId) ?? '';
    const known = new Set(s.strategies.map((x) => x.id));
    for (const strategy of [...s.strategies, null]) {
      const key = strategy?.id ?? '';
      const members = pickable.filter((o) => (strategy ? keyOf(o) === key : !known.has(keyOf(o))));
      if (members.length === 0) continue;
      const ids = new Set(members.map((o) => o.id));
      const rows: { o: StrategicObjective; depth: number }[] = [];
      const seen = new Set<string>();
      const visit = (o: StrategicObjective, depth: number) => {
        if (seen.has(o.id)) return;
        seen.add(o.id);
        rows.push({ o, depth });
        members.filter((c) => c.parentObjectiveId === o.id).forEach((c) => visit(c, depth + 1));
      };
      members.filter((o) => !o.parentObjectiveId || !ids.has(o.parentObjectiveId)).forEach((o) => visit(o, 0));
      groups.push({ strategy, rows });
    }
    return groups;
  }, [s.strategies, pickable, byId]);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Set<string>>(() => new Set(tree.length === 1 ? [tree[0].strategy?.id ?? ''] : []));
  const [pick, setPick] = useState<Pick | null>(null);
  const [strength, setStrength] = useState<AlignmentStrength>(s.groups.length === 0 ? 'primary' : 'secondary');
  const { saving, error, run } = useEditorSave(
    () => (pick ? save(pick.objectiveId, strength, pick.goalId ?? undefined) : Promise.resolve('Pick an objective or a goal first.')),
    close,
  );

  const choose = (p: Pick) => {
    setPick(p);
    const existing = linkedObjective.get(p.objectiveId);
    if (existing) setStrength(existing.strength);
  };
  const isOn = (p: Pick) => pick?.objectiveId === p.objectiveId && pick.goalId === p.goalId;
  const toggle = (key: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const objectiveOption = (o: StrategicObjective, depth: number, path?: string) => {
    const linked = linkedObjective.get(o.id);
    const wholeLinked = !!linked && linked.mappingIds.length > linked.linkedGoals.length;
    return (
      <div key={o.id} style={{ paddingLeft: depth * 16 }}>
        <PickOption
          on={isOn({ objectiveId: o.id, goalId: null })}
          disabled={wholeLinked}
          note={wholeLinked ? 'already linked' : undefined}
          onClick={() => choose({ objectiveId: o.id, goalId: null })}
        >
          <ObjectRef kind="objective" name={o.name} />
          {path && <span className="block text-[12px] text-[var(--text-dim)]">{path}</span>}
        </PickOption>
        {goalsOf(o.id).map((g) => {
          const goalLinked = !!linked?.linkedGoals.some((l) => l.goal.id === g.id);
          return (
            <div key={g.id} className="mt-1" style={{ paddingLeft: 20 }}>
              <PickOption
                on={isOn({ objectiveId: o.id, goalId: g.id })}
                disabled={goalLinked}
                note={goalLinked ? 'already linked' : undefined}
                onClick={() => choose({ objectiveId: o.id, goalId: g.id })}
              >
                <ObjectRef kind="goal" name={g.name} />
                {goalMeasure(g) && <span className="ml-1.5 text-[12px] text-[var(--text-dim)]">{goalMeasure(g)}</span>}
              </PickOption>
            </div>
          );
        })}
      </div>
    );
  };

  const q = query.trim().toLowerCase();
  const strategyName = (o: StrategicObjective) => s.strategies.find((x) => x.id === strategyIdOf(o, byId))?.name;
  const pathOf = (o: StrategicObjective) => [strategyName(o), ...objectivesAbove(o, byId).map((x) => x.name)].filter(Boolean).join(' › ');
  const matches = q
    ? pickable.filter(
        (o) =>
          o.name.toLowerCase().includes(q) ||
          goalsOf(o.id).some((g) => g.name.toLowerCase().includes(q)) ||
          (strategyName(o) ?? '').toLowerCase().includes(q),
      )
    : [];
  const strengthOptions: ChoiceOption[] = (Object.keys(STRENGTH) as AlignmentStrength[]).map((k) => ({
    value: k,
    label: STRENGTH[k].choice,
    description: STRENGTH[k].description,
  }));

  return (
    <div
      className="space-y-3"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
        }
      }}
    >
      <div className="max-w-sm">
        <TextInput
          aria-label="Find a strategy, objective or goal"
          value={query}
          placeholder="Find a strategy, objective or goal…"
          autoFocus
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <div role="radiogroup" aria-label="Objective or goal" className="space-y-1.5">
        {q ? (
          matches.length > 0 ? (
            matches.map((o) => objectiveOption(o, 0, pathOf(o)))
          ) : (
            <span className="text-sm text-[var(--text-dim)]">Nothing matches.</span>
          )
        ) : (
          tree.map(({ strategy, rows }) => {
            const key = strategy?.id ?? '';
            const expanded = open.has(key);
            return (
              <div key={key || 'none'}>
                <button type="button" aria-expanded={expanded} className="flex w-full items-baseline gap-2 py-1 text-left" onClick={() => toggle(key)}>
                  <span aria-hidden className="w-3 text-[var(--text-dim)]">
                    {expanded ? '▾' : '▸'}
                  </span>
                  {strategy ? <ObjectRef kind="strategy" name={strategy.name} /> : <span className="text-sm text-[var(--text-dim)]">Not under a strategy</span>}
                  <span className="text-[12px] text-[var(--text-dim)]">
                    {rows.length} {rows.length === 1 ? 'objective' : 'objectives'}
                  </span>
                </button>
                {expanded && <div className="mt-1 space-y-1.5 pl-5">{rows.map(({ o, depth }) => objectiveOption(o, depth))}</div>}
              </div>
            );
          })
        )}
      </div>
      <div>
        <div className="mb-1.5 text-[13px] text-[var(--text-dim)]">How strongly does this work support it?</div>
        <ChoiceList options={strengthOptions} value={strength} onPick={(v) => setStrength(v as AlignmentStrength)} label="How strongly" disabled={saving} compact />
      </div>
      <EditorError error={error} />
      <EditorButtons onSave={() => void run()} onCancel={close} saving={saving} saveLabel="Link" />
    </div>
  );
}

// ── Rows ────────────────────────────────────────────────────────────────────

export function buildStrategyRows(s: ItemStrategy, a: StrategyActions): { rows: ViewLinkRow[]; empty?: string } {
  const rows: ViewLinkRow[] = s.groups.map((group) => ({
    id: `strategy:${group.strategy?.id ?? 'none'}`,
    kind: 'Strategy',
    text: <GroupText group={group} a={a} />,
  }));

  const link = a.linkObjective;
  const anyPickable = linkableObjectives(s.objectives).length > 0;

  if (s.objectives.length === 0) {
    rows.push({
      id: 'no-objectives',
      kind: 'Supports',
      text: <span className="text-[var(--text-dim)]">This workspace has no objectives yet, so there is nothing to link this work to.</span>,
      actions: [{ label: 'Set objectives', onClick: a.openObjectives }],
    });
  } else if (link && anyPickable) {
    const first = s.groups.length === 0;
    rows.push({
      id: 'link-objective',
      kind: first ? 'Supports' : 'Link another',
      text: first ? null : <span className="text-[var(--text-dim)]">Another objective, or a goal under one, that this work supports.</span>,
      meaning: first ? 'Work that supports no objective has no reason behind its rank.' : undefined,
      editLabel: first ? 'Link an objective or goal' : 'Link',
      editor: (close) => <StrategyLinkEditor s={s} close={close} save={link} />,
    });
  }

  return { rows, empty: 'Supports no objective yet.' };
}
